import * as XLSX from 'xlsx';

/** Expected Products sheet headers (must match server sample). */
export const BULK_PRODUCT_HEADERS = [
  'Product Name',
  'Pack Label',
  'Original Price',
  'Selling Price',
  'Metric',
  'GST Group',
  'Online (Yes/No)',
  'Offline (Yes/No)',
  'Marketplace (Yes/No)',
  'Common Inventory (Yes/No)',
  'Description',
  'Category',
  'HSN',
  'Barcode',
  'Weight Value',
  'Weight Unit',
  'Discount %',
  'Option Barcode',
];

const normalizeHeader = (h) => String(h || '').trim().toLowerCase().replace(/\s+/g, ' ');

/** Map alternate header spellings to canonical column names. */
const HEADER_ALIASES = {
  'product name': 'Product Name',
  'pack label': 'Pack Label',
  pack: 'Pack Label',
  'original price': 'Original Price',
  mrp: 'Original Price',
  'selling price': 'Selling Price',
  price: 'Selling Price',
  metric: 'Metric',
  'gst group': 'GST Group',
  gst: 'GST Group',
  'online (yes/no)': 'Online (Yes/No)',
  online: 'Online (Yes/No)',
  'offline (yes/no)': 'Offline (Yes/No)',
  offline: 'Offline (Yes/No)',
  'marketplace (yes/no)': 'Marketplace (Yes/No)',
  marketplace: 'Marketplace (Yes/No)',
  'common inventory (yes/no)': 'Common Inventory (Yes/No)',
  'common inventory': 'Common Inventory (Yes/No)',
  commoninventory: 'Common Inventory (Yes/No)',
  'shared inventory': 'Common Inventory (Yes/No)',
  description: 'Description',
  category: 'Category',
  hsn: 'HSN',
  barcode: 'Barcode',
  'weight value': 'Weight Value',
  'weight unit': 'Weight Unit',
  'discount %': 'Discount %',
  discount: 'Discount %',
  'option barcode': 'Option Barcode',
};

const SPREADSHEET_EXT =
  /\.(xlsx|xlsm|xlsb|xls|xltx|xltm|csv|ods|xml|numbers)$/i;

const NUMBERS_MIME = [
  'application/vnd.apple.numbers',
  'application/x-iwork-numbers-sffnumbers',
  'application/x-iwork-numbers',
];

/**
 * True for Excel / CSV / ODS / Apple Numbers (incl. macOS files with hidden extension).
 */
export const isBulkSpreadsheetFile = (file) => {
  if (!file) return false;
  const name = String(file.name || '');
  if (SPREADSHEET_EXT.test(name)) return true;
  const type = String(file.type || '').toLowerCase();
  if (NUMBERS_MIME.some((m) => type.includes('numbers') || type === m)) return true;
  if (
    type.includes('spreadsheet') ||
    type.includes('excel') ||
    type === 'text/csv' ||
    type === 'application/csv' ||
    type === 'text/xml' ||
    type === 'application/xml' ||
    type === 'application/vnd.oasis.opendocument.spreadsheet'
  ) {
    return true;
  }
  // macOS Numbers often saves without a visible extension; empty MIME + no ext → allow if name looks like our sample
  if (!/\.[a-z0-9]+$/i.test(name) && /bulk|product|zuntly|sample/i.test(name)) {
    return true;
  }
  return false;
};

const scoreSheetForProducts = (sheet) => {
  const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });
  if (!rows.length) return -1;
  // Find a header-like row in the first few rows (Numbers sometimes inserts a title row)
  let best = -1;
  for (let i = 0; i < Math.min(5, rows.length); i += 1) {
    const cells = (rows[i] || []).map((c) => normalizeHeader(c));
    let score = 0;
    BULK_PRODUCT_HEADERS.forEach((h) => {
      if (cells.includes(normalizeHeader(h))) score += 1;
    });
    // Strong signal
    if (cells.includes('product name')) score += 5;
    if (cells.includes('pack label')) score += 3;
    best = Math.max(best, score);
  }
  return best;
};

const pickProductsSheetName = (workbook) => {
  const names = workbook.SheetNames || [];
  if (!names.length) return null;

  const byName =
    names.find((n) => normalizeHeader(n) === 'products') ||
    names.find((n) => normalizeHeader(n).includes('product'));
  if (byName) return byName;

  // Numbers: one SheetJS sheet per table — pick the table with the best header match
  let bestName = names[0];
  let bestScore = -1;
  names.forEach((n) => {
    const score = scoreSheetForProducts(workbook.Sheets[n]);
    if (score > bestScore) {
      bestScore = score;
      bestName = n;
    }
  });
  return bestName;
};

const mapRowsToCanonical = (rawRows) => {
  const rows = rawRows.map((raw) => {
    const mapped = {};
    Object.keys(raw).forEach((key) => {
      const alias = HEADER_ALIASES[normalizeHeader(key)];
      const canonical = alias || key.trim();
      mapped[canonical] = raw[key];
    });
    return mapped;
  });

  return rows.filter((r) =>
    BULK_PRODUCT_HEADERS.some((h) => String(r[h] ?? '').trim() !== '')
  );
};

/**
 * Parse uploaded spreadsheet including Apple Numbers (.numbers).
 * Returns Products sheet rows as objects with canonical keys.
 */
export const parseBulkProductExcel = async (file) => {
  const data = await file.arrayBuffer();
  let workbook;
  try {
    workbook = XLSX.read(data, { type: 'array', cellDates: true });
  } catch (err) {
    const isNumbers =
      /\.numbers$/i.test(file.name || '') ||
      String(file.type || '').toLowerCase().includes('numbers');
    if (isNumbers) {
      throw new Error(
        'Could not read this Numbers file. In Numbers use File → Export To → Excel…, then upload the .xlsx file.'
      );
    }
    throw new Error(err?.message || 'Could not read this spreadsheet file.');
  }

  const sheetName = pickProductsSheetName(workbook);
  if (!sheetName) {
    throw new Error('No sheet found in the spreadsheet file.');
  }

  const sheet = workbook.Sheets[sheetName];
  // Prefer object rows; if empty, try finding header row manually for Numbers quirks
  let rawRows = XLSX.utils.sheet_to_json(sheet, { defval: '', raw: false });

  if (!rawRows.length) {
    const aoa = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });
    let headerIdx = 0;
    for (let i = 0; i < Math.min(8, aoa.length); i += 1) {
      const cells = (aoa[i] || []).map((c) => normalizeHeader(c));
      if (cells.includes('product name') && cells.includes('pack label')) {
        headerIdx = i;
        break;
      }
    }
    const headers = (aoa[headerIdx] || []).map((h) => String(h || '').trim());
    rawRows = aoa.slice(headerIdx + 1).map((row) => {
      const obj = {};
      headers.forEach((h, idx) => {
        if (h) obj[h] = row[idx] ?? '';
      });
      return obj;
    });
  }

  const rows = mapRowsToCanonical(rawRows);
  if (!rows.length) {
    throw new Error(
      'No product rows found. Make sure the Products table has a header row (Product Name, Pack Label, …) and at least one data row.'
    );
  }
  return rows;
};

export const formatRupee = (n) => {
  const num = Number(n);
  if (!Number.isFinite(num)) return '—';
  return `₹${num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};
